import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ButtonColor } from '@freelancer/ui/button';
import { ContainerSize } from '@freelancer/ui/container';
import { RoundCornerType } from '@freelancer/ui/gallery-mosaic';
import type { GalleryItem } from '@freelancer/ui/gallery-types';
import { GalleryItemType } from '@freelancer/ui/gallery-types';
import { PictureLoadMethod } from '@freelancer/ui/picture';

interface GalleryItemContent extends GalleryItem {
  trackingLabel: string;
  ctaLabel?: string;
  pjpTitlePrefill?: string;
}

@Component({
  standalone: false,
  selector: 'app-home-page-discover-section',
  template: `
    <div [flTrackingSection]="'HomePage'">
      <fl-container
        class="FlexContainer"
        [padding]="false"
        [size]="ContainerSize.FLUID"
      >
        <fl-gallery-mosaic
          class="mb-small tablet:mb-xxxsmall"
          flTrackingLabel="CategoryPageLink"
          [forceRectangleMedium]="true"
          [galleryItems]="galleryItemsLevel"
          [inlineVideos]="true"
          [interactiveContent]="true"
          [linkEntireTile]="true"
          [itemSpacing]="'xsmall'"
          [loadMethod]="PictureLoadMethod.LAZY"
          [reverseGallery]="reverseGallery"
          [roundGalleryCorners]="RoundCornerType.ITEM"
          [zoomOnHover]="false"
        >
          <ng-template let-i>
            <fl-gallery-mosaic-interactive-content>
              <div class="Content">
                <p [ngSwitch]="galleryItemsLevel[i].trackingLabel">
                  <!-- Top Gallery -->
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-YearOfTheLaunch'"
                    i18n="Year of the launch description"
                  >
                    The year you finally launch the thing.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-HumanoidRobotics'"
                    i18n="Humanoid robotics description"
                  >
                    Engineer the future, joint by joint.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-ProductManufacturing'"
                    i18n="Product manufacturing description"
                  >
                    From CAD file to wrist, at scale.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-BrandStrategy'"
                    i18n="Branding strategy description"
                  >
                    A brand worth a second cup.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-MobileAppDevelopment'"
                    i18n="Mobile app development description"
                  >
                    Ship the app you've been sketching on napkins.
                  </ng-container>

                  <!-- Mid Gallery -->
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-InteriorDesign'"
                    i18n="Interior design description"
                  >
                    Walk through it before you build it.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-VideoPostProduction'"
                    i18n="Video post production description"
                  >
                    Make the impossible look effortless.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-ExteriorArchitecture'"
                    i18n="Exterior architecture description"
                  >
                    Old bones, new possibilities.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-CosplayCostumeDesign'"
                    i18n="Cosplay costume design description"
                  >
                    Win the con before you arrive.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-AdvancedPrototyping'"
                    i18n="Advanced prototyping description"
                  >
                    Built for the road less travelled. Or no road at all.
                  </ng-container>

                  <!-- Bottom Gallery -->
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-MechanicalEngineering'"
                    i18n="Mechanical engineering description"
                  >
                    Hands-on help for hands that work.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-VideoGameDevelopment'"
                    i18n="Video game development description"
                  >
                    Your idea. Their thumbs.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-FashionLabelBranding'"
                    i18n="Fashion label branding description"
                  >
                    Wear the brand. Live the brand.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-3DBuildingModeling'"
                    i18n="3D building modeling description"
                  >
                    See it standing before you break ground.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="'CategoryPageLink-AutomotiveModifications'"
                    i18n="Automotive modifications description"
                  >
                    Stock is where the story ends.
                  </ng-container>
                </p>
                <fl-button
                  [flTrackingLabel]="galleryItemsLevel[i].trackingLabel"
                  [color]="ButtonColor.PRIMARY"
                  [nextIcon]="true"
                  [link]="galleryItemsLevel[i].link ?? '/post-project'"
                  [queryParams]="galleryItemsLevel[i].queryParams"
                >
                  @if (galleryItemsLevel[i].ctaLabel) {
                    {{ galleryItemsLevel[i].ctaLabel }}
                  } @else {
                    <ng-container i18n="Button Link to text">
                      Post a Project
                    </ng-container>
                  }
                </fl-button>
              </div>
            </fl-gallery-mosaic-interactive-content>
          </ng-template>
        </fl-gallery-mosaic>
      </fl-container>
    </div>
  `,
  styleUrls: ['./discover-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageDiscoverSectionComponent implements OnInit {
  ButtonColor = ButtonColor;
  ContainerSize = ContainerSize;
  PictureLoadMethod = PictureLoadMethod;
  RoundCornerType = RoundCornerType;

  @Input() reverseGallery = false;
  @Input() galleryContentIdentifier: 'top' | 'middle' | 'bottom';

  galleryItemsLevel: readonly GalleryItemContent[];

  topGalleryItems: readonly GalleryItemContent[] = [
    {
      fileName: 'Year-of-the-Launch.mp4',
      source: 'https://www.f-cdn.com/assets/upload/Year-of-the-Launch.mp4',
      isExternal: true,
      type: GalleryItemType.VIDEO,
      link: '/launch',
      linkLabel: $localize`Year of the launch`,
      trackingLabel: 'CategoryPageLink-YearOfTheLaunch',
      ctaLabel: $localize`Launch Now`,
    },
    {
      fileName: 'robot-shoulder.jpg',
      source: 'home/yotl-top/robot-shoulder.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover humanoid robotics`,
      trackingLabel: 'CategoryPageLink-HumanoidRobotics',
      hoverOverlayColor: '#0268F7',
      hoverOverlayText: $localize`Humanoid Robotics`,
      pjpTitlePrefill:
        "I'm building a humanoid robot and need a robotics engineer to design fully articulating limb assemblies.",
    },
    {
      fileName: 'digital-watch.jpg',
      source: 'home/yotl-top/digital-watch.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      link: '/manufacturing',
      linkLabel: $localize`Discover product manufacturing`,
      trackingLabel: 'CategoryPageLink-ProductManufacturing',
      hoverOverlayColor: '#00855F',
      hoverOverlayText: $localize`Product Manufacturing`,
      ctaLabel: $localize`Make it Real`,
    },
    {
      fileName: 'brand-strategy.jpg',
      source: 'home/yotl-top/brand-strategy.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover brand strategy`,
      trackingLabel: 'CategoryPageLink-BrandStrategy',
      hoverOverlayColor: '#FF8D28',
      hoverOverlayText: $localize`Brand Strategy`,
      pjpTitlePrefill:
        "I'm launching a coffee brand and need a designer to craft an identity that stands out on the shelf.",
    },
    {
      fileName: 'mobile-app.jpg',
      source: 'home/yotl-top/mobile-app.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover mobile app development`,
      trackingLabel: 'CategoryPageLink-MobileAppDevelopment',
      hoverOverlayColor: '#4705B0',
      hoverOverlayText: $localize`Mobile App Development`,
      pjpTitlePrefill:
        'I have an app idea ready to build — I need a developer to design, ship, and launch it on iOS.',
    },
  ];

  middleGalleryItems: readonly GalleryItemContent[] = [
    {
      fileName: 'Freelancer-Work_Interior-Design.mp4',
      source:
        'https://www.f-cdn.com/assets/upload/Freelancer-Work_Interior-Design.mp4',
      isExternal: true,
      type: GalleryItemType.VIDEO,
      linkLabel: $localize`Discover interior design`,
      trackingLabel: 'CategoryPageLink-InteriorDesign',
      hoverOverlayColor: '#FF6165',
      hoverOverlayText: $localize`Interior Design`,
      pjpTitlePrefill:
        'I have building blueprints and need an interior designer to bring the space to life in 3D.',
    },
    {
      fileName: 'tank-tread.jpg',
      source: 'home/yotl-mid/tank-tread.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover advanced prototyping`,
      trackingLabel: 'CategoryPageLink-AdvancedPrototyping',
      hoverOverlayColor: '#0268F7',
      hoverOverlayText: $localize`Advanced Prototyping`,
      pjpTitlePrefill:
        "I'm prototyping an all-terrain vehicle and need a product designer to develop the tread system.",
    },
    {
      fileName: 'film-post-production.jpg',
      source: 'home/yotl-mid/film-post-production.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover video post production`,
      trackingLabel: 'CategoryPageLink-VideoPostProduction',
      hoverOverlayColor: '#FF8D28',
      hoverOverlayText: $localize`Video Post-Production`,
      pjpTitlePrefill:
        "I'm producing a feature film and need a VFX artist to create cinematic CGI sequences.",
    },
    {
      fileName: 'cosplay-helmet.jpg',
      source: 'home/yotl-mid/cosplay-helmet.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover cosplay costume design`,
      trackingLabel: 'CategoryPageLink-CosplayCostumeDesign',
      hoverOverlayColor: '#00855F',
      hoverOverlayText: $localize`Cosplay Costume Design`,
      pjpTitlePrefill:
        "I'm preparing for a major cosplay event and need a designer to build a screen-accurate helmet.",
    },
    {
      fileName: 'ext-architecture.jpg',
      source: 'home/yotl-mid/ext-architecture.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover exterior architecture`,
      trackingLabel: 'CategoryPageLink-ExteriorArchitecture',
      hoverOverlayColor: '#FFCC00',
      hoverOverlayText: $localize`Architectural Design & Render`,
      pjpTitlePrefill:
        "I'm converting a historic building and need a licensed architect to design a modern addition.",
    },
  ];

  bottomGalleryItems: readonly GalleryItemContent[] = [
    {
      fileName: 'humanoid-robot.jpg',
      source: 'home/yotl-bot/humanoid-robot.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover mechanical engineering`,
      trackingLabel: 'CategoryPageLink-MechanicalEngineering',
      hoverOverlayColor: '#00855F',
      hoverOverlayText: $localize`Mechanical Engineering`,
      pjpTitlePrefill:
        "I'm assembling a humanoid robot and need a mechanical engineer for the critical build phase.",
    },
    {
      fileName: 'video-game.jpg',
      source: 'home/yotl-bot/video-game.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover video game development`,
      trackingLabel: 'CategoryPageLink-VideoGameDevelopment',
      hoverOverlayColor: '#0268F7',
      hoverOverlayText: $localize`Video Game Development`,
      pjpTitlePrefill:
        'I have a game concept and need a developer to build it into a mobile gaming experience.',
    },
    {
      fileName: 'fashion-branding.jpg',
      source: 'home/yotl-bot/fashion-branding.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover fashion label branding`,
      trackingLabel: 'CategoryPageLink-FashionLabelBranding',
      hoverOverlayColor: '#FF8D28',
      hoverOverlayText: $localize`Fashion Label Branding`,
      pjpTitlePrefill:
        "I'm refreshing my fashion label and need creative services for branding, photography, and art direction.",
    },
    {
      fileName: '3d-building.jpg',
      source: 'home/yotl-bot/3d-building.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Discover 3D Building Modeling`,
      trackingLabel: 'CategoryPageLink-3DBuildingModeling',
      hoverOverlayColor: '#00C3D0',
      hoverOverlayText: $localize`3D Building Modeling`,
      pjpTitlePrefill:
        "I'm planning a small structure and need a 3D artist to render photoreal visuals of my design.",
    },
    {
      fileName: 'auto-modifications.jpg',
      source: 'home/yotl-bot/auto-modifications.jpg',
      isExternal: false,
      type: GalleryItemType.PICTURE,
      linkLabel: $localize`Automotive Modifications`,
      trackingLabel: 'CategoryPageLink-AutomotiveModifications',
      hoverOverlayColor: '#C55300',
      hoverOverlayText: $localize`Automotive Modifications`,
      pjpTitlePrefill:
        "I'm modifying my vehicle for off-road performance and need an automotive specialist to spec the build.",
    },
  ];

  ngOnInit(): void {
    switch (this.galleryContentIdentifier) {
      case 'top':
        this.galleryItemsLevel = this.withTileLinks(this.topGalleryItems);
        break;

      case 'middle':
        this.galleryItemsLevel = this.withTileLinks(this.middleGalleryItems);
        break;

      case 'bottom':
        this.galleryItemsLevel = this.withTileLinks(this.bottomGalleryItems);
        break;
      default:
    }
  }

  private withTileLinks(
    items: readonly GalleryItemContent[],
  ): readonly GalleryItemContent[] {
    return items.map(item => ({
      ...item,
      queryParams: item.pjpTitlePrefill
        ? { project_title: item.pjpTitlePrefill }
        : undefined,
    }));
  }
}
